if (!customElements.get('shipping-calculator')) {
  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.zip = this.querySelector('.js-zip-input');
      this.zip.addEventListener('keydown', this.handleZipKeydown.bind(this));
      this.button = this.querySelector('button');
      this.button.addEventListener('click', this.handleSubmit.bind(this));
    }

    init() {
      this.country = this.querySelector('.js-country-select');
      this.province = this.querySelector('.js-province-select');
      this.rates = this.querySelector('.js-rates');
      this.errors = this.querySelector('.js-errors');
      this.initialised = true;
    }

    /**
     * Handles submission of the rates calculator.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      evt.preventDefault();
      if (!this.initialised) this.init();

      this.errors.hidden = true;
      this.rates.hidden = true;
      this.button.disabled = true;
      this.button.classList.add('is-loading');

      try {
        const address = await this.getAddressFromZip();
        this.province.value = address.estado;

        // On the product page the rates are for the product being viewed; the cart's own
        // /cart/shipping_rates.json only knows what is already in the cart (empty = "no rates").
        const productForm = this.closest('.product-info')?.querySelector('form.js-product-form');
        if (productForm) {
          this.showRates({ shipping_rates: await this.getProductRates(productForm, address) });
          return;
        }

        const params = new URLSearchParams({
          'shipping_address[zip]': this.zip.value,
          'shipping_address[country]': this.country.value,
          'shipping_address[province]': this.province.value
        });
        const response = await fetch(`${theme.routes.cart}/shipping_rates.json?${params}`);
        const data = await response.json();

        if (response.ok) {
          this.showRates(data);
        } else {
          this.showErrors(data);
        }
      } catch (error) {
        this.showErrors({ zip: error.message });
      } finally {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
      }
    }

    /**
     * Shipping rates for the product on the page, without touching the visitor's cart: a throwaway
     * cart is created through the Storefront API with the selected variant and quantity and the
     * typed CEP, and its delivery options are read. Carrier-calculated rates (Sedex, PAC...) only
     * come in a `@defer` fragment, so the answer is a multipart stream.
     * @param {HTMLFormElement} form - The product form (variant id and quantity).
     * @param {object} address - ViaCEP address of the typed CEP.
     * @returns {Promise<{name: string, price: string}[]>} Rates, cheapest first.
     */
    async getProductRates(form, address) {
      const variantId = form.querySelector('[name="id"]')?.value;
      if (!variantId) throw new Error('Escolha uma variação para calcular o frete.');
      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;

      const endpoint = `https://${window.Shopify.shop}/api/${this.dataset.storefrontVersion}/graphql.json`;
      const headers = {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.dataset.storefrontToken
      };
      const unavailable = new Error('Não foi possível calcular o frete agora. Tente novamente.');

      try {
        const created = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            query: 'mutation($input: CartInput!) { cartCreate(input: $input) { cart { id } userErrors { message } } }',
            variables: {
              input: {
                lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity }],
                delivery: { addresses: [{ selected: true, address: { deliveryAddress: { zip: this.zip.value, countryCode: 'BR', provinceCode: address.uf } } }] }
              }
            }
          })
        }).then((response) => response.json());

        const cart = created.data?.cartCreate?.cart;
        if (!cart) throw unavailable;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { ...headers, Accept: 'multipart/mixed; deferSpec=20220824, application/json' },
          body: JSON.stringify({
            query: 'query($id: ID!) { cart(id: $id) { id ... @defer(label: "rates") { deliveryGroups(first: 5, withCarrierRates: true) { nodes { deliveryOptions { title estimatedCost { amount } } } } } } }',
            variables: { id: cart.id }
          })
        });
        if (!response.ok) throw unavailable;

        // The body is a stream of parts, each a header block followed by a JSON document; the
        // options are in the "incremental" one
        const options = (await response.text()).split('--graphql')
          .map((part) => {
            const start = part.indexOf('{');
            const end = part.lastIndexOf('}');
            return start > -1 && end > start ? JSON.parse(part.slice(start, end + 1)) : null;
          })
          .filter(Boolean)
          .flatMap((part) => part.incremental || [])
          .flatMap((part) => part.data?.deliveryGroups?.nodes || [])
          .flatMap((group) => group.deliveryOptions);

        return options
          .map((option) => ({ name: option.title, price: option.estimatedCost.amount }))
          .sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      } catch (error) {
        throw error === unavailable || error.message.startsWith('Escolha') ? error : unavailable;
      }
    }

    /**
     * Resolves the address of the provided CEP (ViaCEP), whose state Shopify requires.
     * @returns {Promise<object>} ViaCEP address: `estado` (full state name) and `uf` (code).
     */
    async getAddressFromZip() {
      const zip = this.zip.value.replace(/\D/g, '');
      if (!/^\d{8}$/.test(zip)) {
        throw new Error('Informe um CEP válido.');
      }

      const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
      const address = await response.json();
      if (!response.ok || address.erro || !address.estado) {
        throw new Error('CEP não encontrado.');
      }

      return address;
    }

    /**
     * Handles keydown event on the zip code input.
     * @param {object} evt - Event object.
     */
    handleZipKeydown(evt) {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        this.handleSubmit(evt);
      }
    }

    /**
     * Formats and shows a list of available shipping rates.
     * @param {object} data - Response data.
     */
    showRates(data) {
      const headingEl = this.rates.querySelector('.js-rates-heading');
      const textEl = this.rates.querySelector('.js-rates-text');
      textEl.innerHTML = '';

      if (data.shipping_rates && data.shipping_rates.length) {
        const headingLocaleStr = data.shipping_rates.length === 1 ? 'singleRate' : 'multipleRates';
        let rates = '';
        const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: window.Shopify?.currency?.active || 'BRL' });

        data.shipping_rates.forEach((rate) => {
          rates += `<li>${rate.name}: ${money.format(parseFloat(rate.price))}</li>`;
        });

        headingEl.hidden = false;
        headingEl.textContent = theme.strings.shippingCalculator[headingLocaleStr];
        textEl.innerHTML = `<ul class="styled-list">${rates}</ul>`;
      } else {
        headingEl.hidden = true;
        textEl.innerHTML = theme.strings.shippingCalculator.noRates;
      }

      this.rates.hidden = false;
    }

    /**
     * Formats and shows errors relating to rates retrieval.
     * @param {object} data - Response data.
     */
    showErrors(data) {
      let errors = '';

      Object.keys(data).forEach((key) => {
        errors += `<li>${data[key]}</li>`;
      });

      this.errors.querySelector('.js-errors-text').innerHTML = `<ul class="styled-list">${errors}</ul>`;
      this.errors.hidden = false;
    }
  }

  customElements.define('shipping-calculator', ShippingCalculator);
}
